import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { TID } from "@/lib/testIds";
import SeoHead from "@/components/SeoHead";
import FinalCta from "@/components/sections/FinalCta";
import { ArrowUpRight } from "lucide-react";

const BG =
  "https://images.unsplash.com/photo-1582407947304-fd86f028f716?auto=format&fit=crop&w=2200&q=80";

export default function Solutions() {
  const { t } = useI18n();
  const cats = t("solutions_page.categories");

  return (
    <div data-testid={TID.pageSolutions}>
      <SeoHead titleKey="seo.solutions_title" descKey="seo.solutions_desc" />

      <section className="relative bg-[color:var(--gc-navy)] text-white pt-36 pb-24 md:pt-44 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={BG} alt="" className="w-full h-full object-cover gc-kenburns" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--gc-navy)] via-[color:var(--gc-navy)]/85 to-transparent" />
        <div className="absolute inset-0 gc-grid-bg-dark opacity-60" />

        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <p className="gc-eyebrow text-white/60">{t("solutions_page.eyebrow")}</p>
            <h1 className="mt-6 font-display text-5xl md:text-7xl lg:text-[80px] leading-[0.98] tracking-tight font-[700]">
              {t("solutions_page.title")}
            </h1>
            <p className="mt-6 max-w-2xl text-lg md:text-xl text-white/75 leading-relaxed">
              {t("solutions_page.lead")}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-24">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="border-t border-[color:var(--gc-line)]">
            {Array.isArray(cats) &&
              cats.map((c, i) => (
                <motion.article
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.55 }}
                  className="group border-b border-[color:var(--gc-line)] py-12 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 hover:bg-[color:var(--gc-paper)]/40 transition-colors px-2 -mx-2"
                  data-testid={`solution-cat-${c.k}`}
                >
                  <div className="lg:col-span-2 flex lg:flex-col lg:items-start gap-4 lg:gap-2">
                    <span className="font-mono text-sm tracking-[0.18em] text-[color:var(--gc-mist)]">
                      / {c.k}
                    </span>
                    <span className="w-10 h-px bg-[color:var(--gc-red)] mt-2" />
                  </div>
                  <div className="lg:col-span-6">
                    <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-[700] tracking-tight leading-tight text-[color:var(--gc-ink)]">
                      {c.t}
                    </h2>
                    <p className="mt-5 text-base lg:text-lg leading-relaxed text-[color:var(--gc-graphite)] max-w-2xl">
                      {c.d}
                    </p>
                  </div>
                  <div className="lg:col-span-4 space-y-5">
                    <div>
                      <p className="gc-eyebrow text-[color:var(--gc-fog)]">{t("solutions_page.applications_label")}</p>
                      <p className="mt-2 text-sm text-[color:var(--gc-steel)] leading-relaxed">
                        {c.apps}
                      </p>
                    </div>
                    <div>
                      <p className="gc-eyebrow text-[color:var(--gc-fog)]">{t("solutions_page.technical_label")}</p>
                      <p className="mt-2 text-sm text-[color:var(--gc-steel)] leading-relaxed">
                        {c.tech}
                      </p>
                    </div>
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-[color:var(--gc-ink)] border-b border-[color:var(--gc-red)] pb-1 hover:gap-3 transition-all"
                    >
                      {t("common.cta_discuss")}
                      <ArrowUpRight className="w-4 h-4" />
                    </Link>
                  </div>
                </motion.article>
              ))}
          </div>
        </div>
      </section>

      <FinalCta />
    </div>
  );
}
